import React, { useState } from 'react';
import { TaxCodesList } from './TaxCodesList';
import { TaxCodeForm } from './TaxCodeForm';
import { TaxCodeAuditHistory } from './TaxCodeAuditHistory';
import { TaxCodeLifecycleHistory, TaxCodeLifecycleHistory as TaxCodeLifecycleHistoryType } from './TaxCodeLifecycleHistory';
import { TaxTypesList } from './types/TaxTypesList';
import { TaxTypeForm } from './types/TaxTypeForm';
import { PlusIcon, EditIcon, EyeIcon, TrashIcon, CheckCircleIcon, AlertTriangleIcon } from 'lucide-react';
export type TaxCode = {
  id: string;
  code: string;
  description: string;
  rate: number;
  isInclusive: boolean;
  isExempt: boolean;
  country: string;
  taxTypeId: string;
  taxTypeName?: string;
  jurisdiction: string;
  currencyCode: string;
  globalTaxDefinitionId: string;
  scope: 'Global' | 'OrgUnit';
  orgUnitId?: string;
  orgUnitName?: string;
  orgUnitPath?: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  modifiedAt: string;
  modifiedBy: string;
};
export type TaxType = {
  id: string;
  code: string;
  name: string;
  description: string;
  isActive: boolean;
  createdAt: string;
  createdBy: string;
  modifiedAt: string;
  modifiedBy: string;
};
export function TaxCodesManagement({
  locale = 'en-US'
}) {
  const [activeTab, setActiveTab] = useState<'taxCodes' | 'taxTypes'>('taxCodes');
  const [view, setView] = useState<'list' | 'form' | 'audit' | 'lifecycle'>('list');
  const [selectedTaxCode, setSelectedTaxCode] = useState<TaxCode | null>(null);
  const [selectedTaxType, setSelectedTaxType] = useState<TaxType | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [taxCodes, setTaxCodes] = useState<TaxCode[]>([{
    id: 'tax-001',
    code: 'GST10',
    description: 'GST Inclusive',
    rate: 0.1,
    isInclusive: true,
    isExempt: false,
    country: 'AU',
    taxTypeId: 'type-001',
    taxTypeName: 'GST',
    jurisdiction: '',
    currencyCode: 'AUD',
    globalTaxDefinitionId: '',
    scope: 'Global',
    isActive: true,
    createdAt: '2020-01-01T00:00:00Z',
    createdBy: 'System Admin',
    modifiedAt: '2020-01-01T00:00:00Z',
    modifiedBy: 'System Admin'
  }, {
    id: 'tax-002',
    code: 'VAT20',
    description: 'Standard VAT',
    rate: 0.2,
    isInclusive: false,
    isExempt: false,
    country: 'GB',
    taxTypeId: 'type-002',
    taxTypeName: 'VAT',
    jurisdiction: '',
    currencyCode: 'GBP',
    globalTaxDefinitionId: '',
    scope: 'Global',
    isActive: true,
    createdAt: '2020-01-01T00:00:00Z',
    createdBy: 'System Admin',
    modifiedAt: '2020-01-01T00:00:00Z',
    modifiedBy: 'System Admin'
  }, {
    id: 'tax-003',
    code: 'VAT5',
    description: 'Reduced VAT',
    rate: 0.05,
    isInclusive: false,
    isExempt: false,
    country: 'GB',
    taxTypeId: 'type-002',
    taxTypeName: 'VAT',
    jurisdiction: '',
    currencyCode: 'GBP',
    globalTaxDefinitionId: '',
    scope: 'Global',
    isActive: true,
    createdAt: '2020-01-01T00:00:00Z',
    createdBy: 'System Admin',
    modifiedAt: '2020-01-01T00:00:00Z',
    modifiedBy: 'System Admin'
  }, {
    id: 'tax-004',
    code: 'SG-GST8',
    description: 'Singapore GST 8%',
    rate: 0.08,
    isInclusive: true,
    isExempt: false,
    country: 'SG',
    taxTypeId: 'type-001',
    taxTypeName: 'GST',
    jurisdiction: '',
    currencyCode: 'SGD',
    globalTaxDefinitionId: '',
    scope: 'OrgUnit',
    orgUnitId: 'ou-apac-sg',
    orgUnitName: 'Singapore Office',
    orgUnitPath: 'Global Technologies Inc. > APAC Region > Singapore Office',
    isActive: true,
    createdAt: '2020-01-01T00:00:00Z',
    createdBy: 'System Admin',
    modifiedAt: '2020-01-01T00:00:00Z',
    modifiedBy: 'System Admin'
  }, {
    id: 'tax-005',
    code: 'MX-IVA16',
    description: 'Mexico IVA 16%',
    rate: 0.16,
    isInclusive: false,
    isExempt: false,
    country: 'MX',
    taxTypeId: 'type-002',
    taxTypeName: 'VAT',
    jurisdiction: '',
    currencyCode: 'MXN',
    globalTaxDefinitionId: '',
    scope: 'OrgUnit',
    orgUnitId: 'ou-latam-mx',
    orgUnitName: 'Mexico Office',
    orgUnitPath: 'Global Technologies Inc. > LATAM Region > Mexico Office',
    isActive: true,
    createdAt: '2020-01-01T00:00:00Z',
    createdBy: 'System Admin',
    modifiedAt: '2020-01-01T00:00:00Z',
    modifiedBy: 'System Admin'
  }]);
  const [taxTypes, setTaxTypes] = useState<TaxType[]>([{
    id: 'type-001',
    code: 'GST',
    name: 'Goods and Services Tax',
    description: 'Tax on goods and services',
    isActive: true,
    createdAt: '2020-01-01T00:00:00Z',
    createdBy: 'System Admin',
    modifiedAt: '2020-01-01T00:00:00Z',
    modifiedBy: 'System Admin'
  }, {
    id: 'type-002',
    code: 'VAT',
    name: 'Value Added Tax',
    description: 'Tax on value added',
    isActive: true,
    createdAt: '2020-01-01T00:00:00Z',
    createdBy: 'System Admin',
    modifiedAt: '2020-01-01T00:00:00Z',
    modifiedBy: 'System Admin'
  }]);
  const [auditHistory, setAuditHistory] = useState([{
    id: 'audit-001',
    taxCodeId: 'tax-005',
    action: 'Update',
    field: 'rate',
    oldValue: '0.15',
    newValue: '0.16',
    timestamp: '2021-03-15T00:00:00Z',
    userId: 'user-001',
    userName: 'John Smith'
  }]);
  const [lifecycleHistory, setLifecycleHistory] = useState<TaxCodeLifecycleHistoryType[]>([{
    id: 'lifecycle-001',
    taxCodeId: 'tax-001',
    action: 'Activated',
    activeFrom: '2020-01-01',
    activeTo: null,
    effectiveDate: '2020-01-01',
    performedBy: 'System Admin',
    performedAt: '2020-01-01T00:00:00Z'
  }]);
  // Tax Code handlers
  const handleAddTaxCode = () => {
    setSelectedTaxCode(null);
    setView('form');
  };
  const handleEditTaxCode = (taxCode: TaxCode) => {
    setSelectedTaxCode(taxCode);
    setView('form');
  };
  const handleSaveTaxCode = (taxCodeData: Partial<TaxCode>) => {
    if (selectedTaxCode) {
      const updatedTaxCodes = taxCodes.map(tc => tc.id === selectedTaxCode.id ? {
        ...selectedTaxCode,
        ...taxCodeData,
        modifiedAt: new Date().toISOString(),
        modifiedBy: 'Current User'
      } : tc);
      setTaxCodes(updatedTaxCodes);
      const originalTaxCode = taxCodes.find(tc => tc.id === selectedTaxCode.id);
      if (originalTaxCode) {
        const changedFields = Object.keys(taxCodeData).filter(key => taxCodeData[key] !== originalTaxCode[key] && key !== 'modifiedAt' && key !== 'modifiedBy' && key !== 'isActive');
        if (changedFields.length > 0) {
          const newAuditEntries = changedFields.map(field => ({
            id: `audit-${Date.now()}-${field}`,
            taxCodeId: selectedTaxCode.id,
            action: 'Update',
            field,
            oldValue: String(originalTaxCode[field]),
            newValue: String(taxCodeData[field]),
            timestamp: new Date().toISOString(),
            userId: 'user-current',
            userName: 'Current User'
          }));
          setAuditHistory([...auditHistory, ...newAuditEntries]);
        }
      }
    } else {
      const newTaxCode: TaxCode = {
        id: `tax-${Date.now()}`,
        ...taxCodeData,
        isActive: true,
        createdAt: new Date().toISOString(),
        createdBy: 'Current User',
        modifiedAt: new Date().toISOString(),
        modifiedBy: 'Current User'
      } as TaxCode;
      setTaxCodes([...taxCodes, newTaxCode]);
      const today = new Date().toISOString().split('T')[0];
      const historyEntry: TaxCodeLifecycleHistoryType = {
        id: `lifecycle-${Date.now()}`,
        taxCodeId: newTaxCode.id,
        action: 'Activated',
        activeFrom: today,
        activeTo: null,
        effectiveDate: today,
        performedBy: 'Current User',
        performedAt: new Date().toISOString()
      };
      setLifecycleHistory([...lifecycleHistory, historyEntry]);
    }
    setView('list');
    setSelectedTaxCode(null);
  };
  const handleToggleStatus = (taxCodeId: string) => {
    const taxCode = taxCodes.find(tc => tc.id === taxCodeId);
    if (!taxCode) return;
    const today = new Date().toISOString().split('T')[0];
    const updatedTaxCodes = taxCodes.map(tc => {
      if (tc.id === taxCodeId) {
        return {
          ...tc,
          isActive: !tc.isActive,
          modifiedAt: new Date().toISOString(),
          modifiedBy: 'Current User'
        };
      }
      return tc;
    });
    setTaxCodes(updatedTaxCodes);
    const historyEntry: TaxCodeLifecycleHistoryType = {
      id: `lifecycle-${Date.now()}`,
      taxCodeId: taxCodeId,
      action: taxCode.isActive ? 'Deactivated' : 'Reactivated',
      activeFrom: taxCode.isActive ? null : today,
      activeTo: taxCode.isActive ? today : null,
      effectiveDate: today,
      performedBy: 'Current User',
      performedAt: new Date().toISOString()
    };
    setLifecycleHistory([...lifecycleHistory, historyEntry]);
  };
  const handleDeleteTaxCode = (id: string) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };
  const confirmDelete = () => {
    if (deletingId) {
      if (activeTab === 'taxCodes') {
        setTaxCodes(taxCodes.filter(tc => tc.id !== deletingId));
      } else {
        // Check if tax type is in use
        const inUse = taxCodes.some(tc => tc.taxTypeId === deletingId);
        if (inUse) {
          alert('This tax type is in use by one or more tax codes and cannot be deleted.');
          setShowDeleteConfirm(false);
          setDeletingId(null);
          return;
        }
        setTaxTypes(taxTypes.filter(tt => tt.id !== deletingId));
      }
      setShowDeleteConfirm(false);
      setDeletingId(null);
    }
  };
  const handleViewAuditHistory = (taxCode: TaxCode) => {
    setSelectedTaxCode(taxCode);
    setView('audit');
  };
  const handleViewLifecycleHistory = (taxCode: TaxCode) => {
    setSelectedTaxCode(taxCode);
    setView('lifecycle');
  };
  const handleCancelForm = () => {
    setView('list');
    setSelectedTaxCode(null);
    setSelectedTaxType(null);
  };
  // Tax Type handlers
  const handleAddTaxType = () => {
    setSelectedTaxType(null);
    setView('form');
  };
  const handleEditTaxType = (taxType: TaxType) => {
    setSelectedTaxType(taxType);
    setView('form');
  };
  const handleSaveTaxType = (taxTypeData: Partial<TaxType>) => {
    if (selectedTaxType) {
      const updatedTaxTypes = taxTypes.map(tt => tt.id === selectedTaxType.id ? {
        ...selectedTaxType,
        ...taxTypeData,
        modifiedAt: new Date().toISOString(),
        modifiedBy: 'Current User'
      } : tt);
      setTaxTypes(updatedTaxTypes);
      // Update tax type name in associated tax codes
      setTaxCodes(taxCodes.map(tc => tc.taxTypeId === selectedTaxType.id ? {
        ...tc,
        taxTypeName: taxTypeData.name as string,
        modifiedAt: new Date().toISOString(),
        modifiedBy: 'Current User'
      } : tc));
    } else {
      const newTaxType: TaxType = {
        id: `type-${Date.now()}`,
        ...taxTypeData,
        isActive: true,
        createdAt: new Date().toISOString(),
        createdBy: 'Current User',
        modifiedAt: new Date().toISOString(),
        modifiedBy: 'Current User'
      } as TaxType;
      setTaxTypes([...taxTypes, newTaxType]);
    }
    setView('list');
    setSelectedTaxType(null);
  };
  const handleToggleTaxTypeStatus = (taxTypeId: string) => {
    const taxType = taxTypes.find(tt => tt.id === taxTypeId);
    if (!taxType) return;
    const updatedTaxTypes = taxTypes.map(tt => {
      if (tt.id === taxTypeId) {
        return {
          ...tt,
          isActive: !tt.isActive,
          modifiedAt: new Date().toISOString(),
          modifiedBy: 'Current User'
        };
      }
      return tt;
    });
    setTaxTypes(updatedTaxTypes);
  };
  const handleDeleteTaxType = (id: string) => {
    setDeletingId(id);
    setShowDeleteConfirm(true);
  };
  const formatRate = (rate: number) => {
    return `${(rate * 100).toFixed(2)}%`;
  };
  const globalTaxCodes = taxCodes.filter(tc => tc.scope === 'Global');
  const orgUnitTaxCodes = taxCodes.filter(tc => tc.scope === 'OrgUnit');
  const globalTaxTypes = taxTypes.filter(tt => tt.isActive);
  const orgUnitTaxTypes = []; // Empty since we removed org unit scope
  return <div data-id="element-11011">
      <div className="mb-8" data-id="element-11012">
        <h2 className="text-2xl font-bold text-gray-900" data-id="element-11013">
          Tax Codes Management
        </h2>
        <p className="mt-1 text-sm text-gray-500" data-id="element-11014">
          Manage tax codes at global and organisation unit levels for different
          countries and jurisdictions
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6" data-id="element-11015">
        <nav className="-mb-px flex space-x-8" aria-label="Tabs" data-id="element-11016">
          <button className={`${activeTab === 'taxCodes' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} onClick={() => {
          setActiveTab('taxCodes');
          setView('list');
        }} data-id="element-11017">
            Tax Codes
          </button>
          <button className={`${activeTab === 'taxTypes' ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`} onClick={() => {
          setActiveTab('taxTypes');
          setView('list');
        }} data-id="element-11018">
            Tax Types
          </button>
        </nav>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'taxCodes' && <>
          {view === 'list' ? <>
              {/* Global Tax Codes */}
              <div className="bg-white shadow rounded-lg mb-6" data-id="element-11019">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-11020">
                  <div className="flex items-center justify-between" data-id="element-11021">
                    <div data-id="element-11022">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11023">
                        Global Tax Codes
                      </h3>
                      <p className="mt-1 text-sm text-gray-500" data-id="element-11024">
                        Global tax codes available to all organisation units
                      </p>
                    </div>
                    <button onClick={handleAddTaxCode} className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500" data-id="element-11025">
                      <PlusIcon className="h-4 w-4 mr-2" data-id="element-11026" />
                      Add Tax Code
                    </button>
                  </div>
                </div>

                {globalTaxCodes.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-11027">
                    <p className="text-sm text-gray-500" data-id="element-11028">
                      No global tax codes configured.
                    </p>
                  </div> : <div className="overflow-x-auto" data-id="element-11029">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-11030">
                      <thead className="bg-gray-50" data-id="element-11031">
                        <tr data-id="element-11032">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11033">
                            Code
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11034">
                            Description
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11035">
                            Rate
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11036">
                            Country
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11037">
                            Type
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11038">
                            Status
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11039">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-11040">
                        {globalTaxCodes.map(taxCode => <tr key={taxCode.id} data-id="element-11041">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-11042">
                              {taxCode.code}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-11043">
                              {taxCode.description}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-11044">
                              {taxCode.isExempt ? 'Exempt' : formatRate(taxCode.rate)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-11045">
                              {taxCode.country}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-11046">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-11047">
                                {taxCode.taxTypeName}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-11048">
                              {taxCode.isActive ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-11049">
                                  <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-11050" />
                                  Active
                                </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-11051">
                                  Inactive
                                </span>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-11052">
                              <div className="flex justify-end space-x-3" data-id="element-11053">
                                <button onClick={() => handleViewLifecycleHistory(taxCode)} className="text-green-600 hover:text-green-900" title="View lifecycle history" data-id="element-11054">
                                  <EyeIcon className="h-4 w-4" data-id="element-11055" />
                                  <span className="sr-only" data-id="element-11056">View</span>
                                </button>
                                <button onClick={() => handleEditTaxCode(taxCode)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-11057">
                                  <EditIcon className="h-4 w-4" data-id="element-11058" />
                                  <span className="sr-only" data-id="element-11059">Edit</span>
                                </button>
                                <button onClick={() => handleDeleteTaxCode(taxCode.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-11060">
                                  <TrashIcon className="h-4 w-4" data-id="element-11061" />
                                  <span className="sr-only" data-id="element-11062">Delete</span>
                                </button>
                              </div>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>}
              </div>

              {/* OrgUnit Tax Codes */}
              <div className="bg-white shadow rounded-lg" data-id="element-11063">
                <div className="px-4 py-5 sm:px-6 border-b border-gray-200" data-id="element-11064">
                  <div className="flex items-center justify-between" data-id="element-11065">
                    <div data-id="element-11066">
                      <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11067">
                        Organisation Unit Tax Codes
                      </h3>
                      <p className="mt-1 text-sm text-gray-500" data-id="element-11068">
                        Tax codes specific to individual organisation units
                      </p>
                    </div>
                  </div>
                </div>

                {orgUnitTaxCodes.length === 0 ? <div className="px-4 py-12 text-center" data-id="element-11069">
                    <p className="text-sm text-gray-500" data-id="element-11070">
                      No organisation unit-specific tax codes configured.
                    </p>
                  </div> : <div className="overflow-x-auto" data-id="element-11071">
                    <table className="min-w-full divide-y divide-gray-200" data-id="element-11072">
                      <thead className="bg-gray-50" data-id="element-11073">
                        <tr data-id="element-11074">
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11075">
                            Organisation Unit
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11076">
                            Code
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11077">
                            Description
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11078">
                            Rate
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11079">
                            Country
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11080">
                            Status
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-11081">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200" data-id="element-11082">
                        {orgUnitTaxCodes.map(taxCode => <tr key={taxCode.id} data-id="element-11083">
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-11084">
                              <div className="text-sm font-medium text-gray-900" data-id="element-11085">
                                {taxCode.orgUnitName}
                              </div>
                              <div className="text-sm text-gray-500" data-id="element-11086">
                                {taxCode.orgUnitPath}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-11087">
                              {taxCode.code}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-11088">
                              {taxCode.description}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900" data-id="element-11089">
                              {taxCode.isExempt ? 'Exempt' : formatRate(taxCode.rate)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-11090">
                              {taxCode.country}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap" data-id="element-11091">
                              {taxCode.isActive ? <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-11092">
                                  <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-11093" />
                                  Active
                                </span> : <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-11094">
                                  Inactive
                                </span>}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-11095">
                              <div className="flex justify-end space-x-3" data-id="element-11096">
                                <button onClick={() => handleViewLifecycleHistory(taxCode)} className="text-green-600 hover:text-green-900" title="View lifecycle history" data-id="element-11097">
                                  <EyeIcon className="h-4 w-4" data-id="element-11098" />
                                  <span className="sr-only" data-id="element-11099">View</span>
                                </button>
                                <button onClick={() => handleEditTaxCode(taxCode)} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-11100">
                                  <EditIcon className="h-4 w-4" data-id="element-11101" />
                                  <span className="sr-only" data-id="element-11102">Edit</span>
                                </button>
                                <button onClick={() => handleDeleteTaxCode(taxCode.id)} className="text-red-600 hover:text-red-900" title="Delete" data-id="element-11103">
                                  <TrashIcon className="h-4 w-4" data-id="element-11104" />
                                  <span className="sr-only" data-id="element-11105">Delete</span>
                                </button>
                              </div>
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>}
              </div>
            </> : view === 'form' ? <TaxCodeForm taxCode={selectedTaxCode} onSave={handleSaveTaxCode} onCancel={handleCancelForm} onToggleStatus={handleToggleStatus} locale={locale} auditHistory={selectedTaxCode ? auditHistory.filter(entry => entry.taxCodeId === selectedTaxCode.id) : []} lifecycleHistory={selectedTaxCode ? lifecycleHistory.filter(entry => entry.taxCodeId === selectedTaxCode.id) : []} taxTypes={taxTypes} data-id="element-11106" /> : view === 'audit' && selectedTaxCode ? <TaxCodeAuditHistory taxCode={selectedTaxCode} auditEntries={auditHistory.filter(entry => entry.taxCodeId === selectedTaxCode.id)} onBack={handleCancelForm} locale={locale} data-id="element-11107" /> : view === 'lifecycle' && selectedTaxCode ? <TaxCodeLifecycleHistory taxCode={selectedTaxCode} lifecycleHistory={lifecycleHistory.filter(entry => entry.taxCodeId === selectedTaxCode.id)} onBack={handleCancelForm} locale={locale} data-id="element-11108" /> : null}
        </>}

      {activeTab === 'taxTypes' && <>
          {view === 'form' ? <TaxTypeForm taxType={selectedTaxType} onSave={handleSaveTaxType} onCancel={handleCancelForm} onToggleStatus={handleToggleTaxTypeStatus} locale={locale} data-id="element-11109" /> : <TaxTypesList globalTaxTypes={globalTaxTypes} orgUnitTaxTypes={orgUnitTaxTypes} onAdd={handleAddTaxType} onEdit={handleEditTaxType} onDelete={handleDeleteTaxType} locale={locale} data-id="element-11110" />}
        </>}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && <div className="fixed z-10 inset-0 overflow-y-auto" data-id="element-11111">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0" data-id="element-11112">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true" data-id="element-11113">
              <div className="absolute inset-0 bg-gray-500 opacity-75" data-id="element-11114"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" data-id="element-11115">
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full" data-id="element-11116">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4" data-id="element-11117">
                <div className="sm:flex sm:items-start" data-id="element-11118">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10" data-id="element-11119">
                    <AlertTriangleIcon className="h-6 w-6 text-red-600" data-id="element-11120" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" data-id="element-11121">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" data-id="element-11122">
                      Delete{' '}
                      {activeTab === 'taxCodes' ? 'Tax Code' : 'Tax Type'}
                    </h3>
                    <div className="mt-2" data-id="element-11123">
                      <p className="text-sm text-gray-500" data-id="element-11124">
                        Are you sure you want to delete this{' '}
                        {activeTab === 'taxCodes' ? 'tax code' : 'tax type'}?
                        This action cannot be undone.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse" data-id="element-11125">
                <button type="button" onClick={confirmDelete} className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-11126">
                  Delete
                </button>
                <button type="button" onClick={() => setShowDeleteConfirm(false)} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-11127">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}